import { DateTime } from 'luxon';
import {
  Order,
  OrderReturnStatus,
  OrderSource,
  OrderStatus,
  OrderType,
} from '../../clients/quipli-service/order/order';
import { Location } from '../../clients/quipli-service/location/location';
import Icon from '../../components/icons/icon';
import { utils, write } from 'xlsx';
import FileSaver from 'file-saver';
import { OrderPaymentType } from '../../clients/quipli-service/payment/payment';
import { Invoice, LineItemType } from '../../clients/quipli-service/invoice/invoice';
import _ from 'lodash';
import { UserAddress } from '../../clients/quipli-service/user/user';
import { ShippingType } from '../../clients/quipli-service/delivery/delivery';
import { ProductTrackingType } from '../../clients/quipli-service/product/product';

export const orderExcludedComments: string[] = [
  'Security Deposit',
  'Partial Payment',
  'Order Updated',
  'Renter Order Extension Request',
  'Renter order Call-off Request',
  'Admin Order Extension Request',
  'Admin order Call-off Request',
];

export function formatAddress(address: UserAddress): string {
  if (address.address1.indexOf(',') >= 0) {
    return address.address1;
  } else {
    return `${address.address1}, ${address.address2}, ${address.city}, ${address.state}, ${address.zip}`;
  }
}

export function getOrderTotalTaxes(order: Order): number {
  const totalUpchargesTax = order.payments
    .filter((op) => [OrderPaymentType.UpchargePayment].indexOf(op.paymentType) >= 0)
    .map((op) => op.taxCharged || 0)
    .reduce((sum, p) => sum + p, 0);

  return order.taxCharged + totalUpchargesTax;
}

export function getOrderAdditionalFees(order: Order): number {
  return order.additionalFees.reduce((total, fee) => total + fee.feeAppliedValue, 0);
}

export function getOrderProductFees(order: Order): number {
  const fees = order.products.flatMap((op) => op.additionalFees);
  const totalFeeValue = fees.reduce((total, fee) => total + fee.feeAppliedValue, 0);

  return totalFeeValue;
}

export function getOrderTotalUpcharges(order: Order): number {
  const totalUpcharges = order.payments
    .filter((op) => !orderExcludedComments.includes(op.comments as string))
    .filter((op) => [OrderPaymentType.UpchargePayment, OrderPaymentType.Captured].indexOf(op.paymentType) >= 0)
    .map((op) => op.amount)
    .reduce((sum, p) => sum + p, 0);

  return totalUpcharges;
}

export function getOrderTotalRefunds(order: Order): number {
  const totalRefunds = order.payments
    .filter((op) => [OrderPaymentType.Refund].indexOf(op.paymentType) >= 0)
    .flatMap((op) => {
      if (!op.comments?.includes('security_deposit_refund') && op.comments !== 'Order Updated (Security Deposit)') {
        return op.amount;
      } else {
        return 0;
      }
    })
    .reduce((sum, p) => sum + p, 0);

  return totalRefunds;
}

export function getOrderTotalAmount(order: Order): number {
  const upcharges = getOrderTotalUpcharges(order);
  const refunds = getOrderTotalRefunds(order);

  const orderPayment = order.payments
    .filter((op) => [OrderPaymentType.OrderPayment].indexOf(op.paymentType) >= 0)
    .map((op) => op.amount)
    .reduce((sum, p) => sum + p, 0);

  const totalAmount = [OrderStatus.Cancelled, OrderStatus.Missed].includes(order.status)
    ? orderPayment + upcharges - refunds
    : order.netCharged + upcharges - refunds;

  return totalAmount;
}

export type OrdersTotals = {
  grandTotal?: number;
  noOrders?: number;
  productFeesTotal?: number;
  additionalFeesTotal?: number;
  taxesTotal?: number;
  deliveryFeesTotal?: number;
  damageWaiverFeesTotal?: number;
  discountTotal?: number;
  upchargesTotal?: number;
  refundsTotal?: number;
};

export function calculateOrderTotals(orders: Order[]): OrdersTotals {
  let grandTotal = 0;
  let productFeesTotal = 0;
  let additionalFeesTotal = 0;
  let taxesTotal = 0;
  let deliveryFeesTotal = 0;
  let damageWaiverFeesTotal = 0;
  let discountTotal = 0;
  let upchargesTotal = 0;
  let refundsTotal = 0;

  for (const order of orders) {
    grandTotal += getOrderTotalAmount(order);
    productFeesTotal += getOrderProductFees(order);
    additionalFeesTotal += getOrderAdditionalFees(order);
    taxesTotal += getOrderTotalTaxes(order) || 0;
    deliveryFeesTotal += order.shippingCharged || 0;
    damageWaiverFeesTotal += order.damageWaiver || 0;
    discountTotal += order.discountTotal || 0;
    upchargesTotal += getOrderTotalUpcharges(order);
    refundsTotal += getOrderTotalRefunds(order);
  }

  return {
    grandTotal,
    noOrders: orders.length,
    productFeesTotal,
    additionalFeesTotal,
    taxesTotal,
    deliveryFeesTotal,
    damageWaiverFeesTotal,
    discountTotal,
    upchargesTotal,
    refundsTotal,
  };
}

export const getOrderSourceIcon = (source: OrderSource) => {
  switch (source) {
    case OrderSource.Online:
      return <Icon title={'Online'} icon="World" />;
    case OrderSource.AdminPhone:
      return <Icon title={'Admin Phone'} icon="Laptop" />;
    case OrderSource.InStore:
      return <Icon title={'In Store'} icon="House" />;
    case OrderSource.Quote:
      return <Icon title={'Quote'} icon="File" />;
  }
};

export function getOrderStatusLabel(order: Order): string {
  switch (order.status) {
    case OrderStatus.Requested:
      if (order.products.every((op) => op.type === OrderType.Sale)) {
        return 'Sale Request';
      } else {
        return 'Reservation Request';
      }
    case OrderStatus.Approved:
      switch (order.type) {
        case OrderType.Sale:
          return 'Closed';
        case OrderType.Rental: {
          const rentalProducts = order.products.filter((op) => op.type === OrderType.Rental);
          if (rentalProducts.every((op) => op.status === OrderReturnStatus.Complete)) {
            return 'Rental Ended';
          } else if (rentalProducts.every((op) => op.status === OrderReturnStatus.OffRent)) {
            return 'Off Rent';
          } else if (rentalProducts.some((op) => op.status === OrderReturnStatus.OnRent)) {
            return 'On Rent';
          } else {
            if (order.documentType === 'CreditMemo') {
              return 'Approved';
            }
            return 'Reservation Approved';
          }
        }
      }
      break;
    case OrderStatus.Quote:
      return 'Quote';
    case OrderStatus.Cancelled:
      return 'Cancelled';
    case OrderStatus.Missed:
      return 'Missed';
    default:
      return '';
  }
}

export type OrderTableColumn =
  | 'OrderNumber'
  | 'CustomerName'
  | 'RequestedTime'
  | 'Location'
  | 'Delivery'
  | 'RentalStart'
  | 'RentalEnd'
  | 'Products'
  | 'Amount'
  | 'Status'
  | 'Action'
  | 'ProductName'
  | 'AssignedUnit'
  | 'PastDue'
  | 'Notes'
  | 'CancelledTime'
  | 'CancelledReason';

export function exportToCSV(
  orders: Order[],
  locations: Location[],
  exportFileName: string,
  columns: OrderTableColumn[],
  unitTrackingField: 'serialNumber' | 'unitNumber',
) {
  const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';
  const json = orders?.map((o) => {
    let row = {};
    columns.forEach((col) => {
      switch (col) {
        case 'OrderNumber':
          row = { ...row, 'Order No.': o.orderNumber || 'N/A' };
          break;
        case 'CustomerName':
          row = { ...row, Customer: o.user.name };
          break;
        case 'RequestedTime':
          row = { ...row, Requested: o.dateAdded.toFormat('D t') };
          break;
        case 'Location':
          row = { ...row, Location: locations.find((l) => l.id === o.locationId)?.name };
          break;
        case 'Delivery':
          {
            const shippingTypes = o.products
              .map((op) => op.shippingType)
              .filter((v, i, a) => a.findIndex((o) => o === v) === i);
            const address =
              shippingTypes.some((st) => st !== ShippingType.SelfPickUp) && o.shippingAddress
                ? formatAddress(o.shippingAddress)
                : '';
            row = { ...row, Delivery: shippingTypes.join(', '), 'Delivery Address': address };
          }
          break;
        case 'RentalStart':
          console.log(o.products.map((op) => op.actualRentalStartDate || op.rentalStartDate));
          row = {
            ...row,
            'Rental Start': DateTime.min(
              ...o.products.map((op) => op.actualRentalStartDate || op.rentalStartDate),
            )?.toFormat('D t'),
          };
          break;
        case 'RentalEnd':
          row = {
            ...row,
            'Rental End': o.products.some(
              (op) => op.type === OrderType.Rental && !op.rentalEndDate && !op.actualRentalEndDate,
            )
              ? ''
              : DateTime.max(
                  ...o.products.flatMap((op) =>
                    op.actualRentalEndDate || op.rentalEndDate ? [op.actualRentalEndDate || op.rentalEndDate!] : [],
                  ),
                )?.toFormat('D t'),
          };
          break;
        case 'Products':
          row = {
            ...row,
            Products: o.products
              .map((op) => {
                let unitsString: string = '';

                switch (op.product.trackingType) {
                  case ProductTrackingType.SerialUnits:
                    unitsString = op.assignedUnits.length
                      ? op.assignedUnits.map((au) => `- ${au[unitTrackingField]}`).join(',')
                      : '--';
                    break;
                  case ProductTrackingType.Quantity:
                    unitsString = `${op.quantity} unit(s)`;
                    break;
                  default:
                    unitsString = '';
                    break;
                }

                return `${op.name}:${unitsString}\n`;
              })
              .join(','),
          };
          break;
        case 'Status':
          row = { ...row, Status: getOrderStatusLabel(o) };
          break;
        case 'Amount':
          {
            const fmt = Intl.NumberFormat(undefined, {
              style: 'currency',
              currency: locations.find((l) => l.id === o.id)?.currency || 'USD',
            });
            const amount = fmt.format(o.netCharged);
            row = { ...row, Amount: amount };
          }
          break;
        case 'Notes':
          row = { ...row, Notes: o.notes };
          break;
        case 'Action':
        case 'ProductName':
        case 'AssignedUnit':
        case 'PastDue':
          break;
      }
    });
    return row;
  });
  const ws = utils.json_to_sheet(json || []);
  const wb = { Sheets: { [exportFileName]: ws }, SheetNames: [exportFileName] };
  const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, exportFileName + fileExtension);
}

export function sumByLineItemType(invoices: Invoice[], lineItemType: LineItemType): number {
  return _.sum(
    invoices
      .flatMap((o) => o.lineItems)
      .filter((li) => li.type === lineItemType)
      .map((li) => li.total),
  );
}
